type SyncStatus = {
    lastSync: string | null;
    outboxCount: number;
    conflictCount: number;
    logCount: number;
};

type RelayStats = {
    active: boolean;
    port: number | null;
    addresses: string[];
    requestCount: number;
    uniqueClients: number;
    upstreamBaseUrl?: string | null;
};

type LocalDataPayload = {
    session: any;
    bundle: any;
    lastSync: string | null;
    cloudUrl: string;
    outbox: any[];
    /** Row counts per collection — never the collections themselves. */
    bundleCounts?: Record<string, number | Record<string, number>>;
};

function bridge() {
    return window.examClient;
}

export function isElectronServerRuntime() {
    return typeof window !== 'undefined' && !!bridge()?.server;
}

/**
 * The activation key this server was licensed with, if it has been given one.
 *
 * The main process has had get/validate/clear handlers for this all along, and
 * the preload exposes them -- but nothing in the interface ever called them, so
 * the Institute EXE never asked for a key and ran unlicensed. These are the
 * missing half.
 */
export async function getServerLicense(): Promise<{ key: string | null; instituteId: string | null; configured?: boolean } | null> {
    if (!isElectronServerRuntime()) return null;
    const fn = (bridge() as any)?.server?.getServerLicense;
    if (typeof fn !== 'function') return null;
    try { return await fn(); } catch { return null; }
}

export async function activateServerLicense(licenseKey: string): Promise<{ success: boolean; error?: string; data?: any }> {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running in the Institute EXE.' };
    const fn = (bridge() as any)?.server?.validateServerLicense;
    if (typeof fn !== 'function') {
        return { success: false, error: 'This version cannot activate a licence. Please update the Institute EXE.' };
    }
    try { return await fn({ licenseKey }); }
    catch (e: any) { return { success: false, error: e?.message || 'Activation failed.' }; }
}

export async function getExeSyncStatus(): Promise<SyncStatus | null> {
    if (!isElectronServerRuntime()) return null;
    return await bridge()!.server!.getSyncStatus!();
}

export async function getExeRelayStats(): Promise<RelayStats | null> {
    if (!isElectronServerRuntime()) return null;
    return await bridge()!.server!.relayStats!();
}

export async function getExeLocalData(): Promise<LocalDataPayload | null> {
    if (!isElectronServerRuntime()) return null;
    return await bridge()!.server!.getLocalData!();
}

// ── Cloudflare tunnel ───────────────────────────────────────────────────────
// The same four operations the cloud console can perform, so an operator at
// the machine is never worse off than one working remotely.

export type ExeTunnelState = {
    status: 'stopped' | 'starting' | 'running' | 'error' | 'unavailable';
    url: string | null;
    error?: string | null;
    started_at?: string | null;
    auto?: boolean;
    access_token?: string | null;
    binary_ready?: boolean;
};

/** Live performance figures from the EXE's own monitor. */
export async function getExeSystemReport(): Promise<any | null> {
    if (!isElectronServerRuntime()) return null;
    const api = bridge()!.server as any;
    if (typeof api?.systemReport !== 'function') return null;   // older EXE build
    const res = await api.systemReport();
    return res?.data ?? null;
}

export async function getExeTunnelStatus(): Promise<ExeTunnelState | null> {
    if (!isElectronServerRuntime()) return null;
    const api = bridge()!.server as any;
    if (typeof api?.tunnelStatus !== 'function') return null;   // older EXE build
    const res = await api.tunnelStatus();
    return res?.data ?? null;
}

export async function startExeTunnel() {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    const api = bridge()!.server as any;
    if (typeof api?.tunnelStart !== 'function') {
        return { success: false, error: 'This Institute EXE build has no tunnel support — update it first.' };
    }
    return await api.tunnelStart();
}

export async function stopExeTunnel() {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    const api = bridge()!.server as any;
    if (typeof api?.tunnelStop !== 'function') return { success: false, error: 'No tunnel support in this build.' };
    return await api.tunnelStop();
}

export async function setExeTunnelAuto(enabled: boolean) {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    const api = bridge()!.server as any;
    if (typeof api?.tunnelSetAuto !== 'function') return { success: false, error: 'No tunnel support in this build.' };
    return await api.tunnelSetAuto(enabled);
}

/** Live updates as the tunnel comes up, drops, or is restarted. */
export function onExeTunnelChanged(cb: (state: ExeTunnelState) => void): () => void {
    if (!isElectronServerRuntime()) return () => {};
    const api = bridge()!.server as any;
    if (typeof api?.onTunnelChanged !== 'function') return () => {};
    return api.onTunnelChanged(cb) || (() => {});
}

export async function triggerExeSync(full = false) {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    return await bridge()!.server!.sync!({ full });
}

export async function getExeSyncLog() {
    if (!isElectronServerRuntime()) return [];
    const result = await bridge()!.server!.getSyncLog!();
    return Array.isArray(result?.log) ? result.log : [];
}

export async function getExeConflicts() {
    if (!isElectronServerRuntime()) return [];
    const result = await bridge()!.server!.getConflicts!();
    return Array.isArray(result?.conflicts) ? result.conflicts : [];
}

export async function resolveExeConflict(conflictId: string, resolution: 'local' | 'cloud') {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    return await bridge()!.server!.resolveConflict!({ conflictId, resolution });
}

export async function clearExeSyncLog() {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    return await bridge()!.server!.clearSyncLog!();
}

export async function getExeDeadLetter() {
    if (!isElectronServerRuntime()) return [];
    const result = await bridge()!.server!.getDeadLetter?.();
    return Array.isArray(result?.items) ? result.items : [];
}

export async function clearExeDeadLetter() {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    return await bridge()!.server!.clearDeadLetter?.();
}

export async function discardExeOutboxItem(itemId: string) {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    const raw = await bridge()!.store!.get!('outbox');
    const list = Array.isArray(raw) ? raw : Array.isArray(raw?.value) ? raw.value : [];
    const next = list.filter((item: any) => item?.id !== itemId);
    await bridge()!.store!.set!('outbox', next);
    return { success: true };
}

export async function getExeLabSnapshot() {
    if (!isElectronServerRuntime()) return null;
    const result = await bridge()!.server!.getLabLive!();
    if (!result?.success) return null;
    return result;
}

export async function requestExeLabScreenshot(pcLabel: string) {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    return await bridge()!.server!.requestLabScreenshot!({ pcLabel });
}

export async function setExeSessionOverride(studentId: string, extendMinutes: number) {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    return await bridge()!.server!.setSessionOverride!({ student_id: studentId, extend_minutes: extendMinutes });
}

// Force-release a lab seat from Lab Monitoring: clears the local relay session
// (which is what frees the PC card) and queues the cloud attendance check-out.
export async function forceExeLabCheckout(pcLabel: string, studentId?: string | null) {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    return await bridge()!.server!.labForceCheckout!({ pc_label: pcLabel, student_id: studentId ?? null });
}

export async function getExeStudentsBatch(): Promise<any[]> {
    if (!isElectronServerRuntime()) return [];
    const stats = await getExeRelayStats();
    const base = getRelayBaseUrl(stats);
    if (!base) return [];
    const res = await fetch(`${base}/relay/students-batch`).then((r) => r.json()).catch(() => null);
    return Array.isArray(res?.data) ? res.data : [];
}

export async function setExeBatchRescheduleToday(studentId: string, untilTime: string) {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    const stats = await getExeRelayStats();
    const base = getRelayBaseUrl(stats);
    if (!base) return { success: false, error: 'Relay not running' };
    return await fetch(`${base}/relay/set-override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: studentId, until_time: untilTime }),
    }).then((r) => r.json()).catch((e) => ({ success: false, error: e?.message }));
}

// Cancel an active batch-reschedule override — the student reverts to their normal
// batch window immediately.
export async function clearExeBatchRescheduleToday(studentId: string) {
    if (!isElectronServerRuntime()) return { success: false, error: 'Not running inside Institute EXE' };
    const stats = await getExeRelayStats();
    const base = getRelayBaseUrl(stats);
    if (!base) return { success: false, error: 'Relay not running' };
    return await fetch(`${base}/relay/clear-override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: studentId }),
    }).then((r) => r.json()).catch((e) => ({ success: false, error: e?.message }));
}

export function pickBestRelayAddress(addresses: string[] = []) {
    return addresses.find((value) => /^192\.168\.|^10\.|^172\.(1[6-9]|2\d|3[0-1])\./.test(value)) || addresses[0] || null;
}

export function getRelayBaseUrl(stats: RelayStats | null) {
    if (!stats?.active || !stats.port) return null;
    const bestAddress = pickBestRelayAddress(stats.addresses || []);
    return bestAddress ? `http://${bestAddress}:${stats.port}` : null;
}

// ── Phase 2: offline content files (multi-provider storage) ──────────────────
// The Institute EXE downloads the institute's registered content files into a local
// cache and serves them over the LAN relay (GET /content/:id). These helpers let
// the content library view read from that offline cache instead of the provider
// embed URL when running inside the Institute EXE runtime.

export type ExeContentFile = {
    id: string;
    provider: string;
    name: string;
    mime_type: string | null;
    size_bytes: number | null;
    checksum: string | null;
    web_view_link: string | null;
    embed_url: string | null;
    cached: boolean;
    sha256: string | null;
};

// The registered content files with their LOCAL cache status. Null when not
// running under the Institute EXE.
export async function getExeContentFiles(): Promise<ExeContentFile[] | null> {
    if (!isElectronServerRuntime()) return null;
    const result = await bridge()!.server!.contentList?.();
    return Array.isArray(result?.files) ? (result.files as ExeContentFile[]) : [];
}

// The LAN relay URL that streams a locally-cached content file, or null when not
// under the Institute EXE / the relay isn't running.
export async function getExeContentUrl(contentId: string): Promise<string | null> {
    if (!isElectronServerRuntime()) return null;
    const stats = await getExeRelayStats();
    const base = getRelayBaseUrl(stats);
    return base ? `${base}/content/${encodeURIComponent(contentId)}` : null;
}
